/**
 * Charms are the staked powers. Signatures are not offered.
 * Tiles of the player's own critter color fill the meter. A full meter
 * fires without spending a move, then the meter can fill again.
 */

export type CharmTier = 'twig' | 'copper' | 'silver' | 'gold' | 'heartwood' | 'starlit';

export const TIER_ORDER: readonly CharmTier[] = [
  'twig',
  'copper',
  'silver',
  'gold',
  'heartwood',
  'starlit',
];

export const TIER_NAME: Record<CharmTier, string> = {
  twig: 'Moss',
  copper: 'Copper',
  silver: 'Silver',
  gold: 'Gold',
  heartwood: 'Heartwood',
  starlit: 'Starlit',
};

/** Tiles of the player's color required to fill the meter. */
export const TIER_CHARGE: Record<CharmTier, number> = {
  twig: 12,
  copper: 12,
  silver: 12,
  gold: 13,
  heartwood: 14,
  starlit: 15,
};

export const TIER_PRICE: Record<CharmTier, { acorns: number | null; honey: number | null }> = {
  twig: { acorns: 0, honey: null },
  copper: { acorns: 120, honey: 15 },
  silver: { acorns: 250, honey: 30 },
  gold: { acorns: 500, honey: 60 },
  heartwood: { acorns: 1200, honey: 140 },
  starlit: { acorns: null, honey: 400 },
};

export const TIER_COLOR: Record<CharmTier, number> = {
  twig: 0x8d6a43,
  copper: 0xc46a3a,
  silver: 0xb7c4d4,
  gold: 0xf0c14a,
  heartwood: 0x6b3f86,
  starlit: 0x7ec8ff,
};

/** A lost Twig Charm regrows after this long. */
export const TWIG_REGROW_MS = 60 * 60 * 1000;
/** Skip the Twig wait. */
export const TWIG_SKIP_ACORNS = 30;

export type CharmAim = 'row' | 'column' | 'cell' | 'line' | 'wave' | 'color' | 'picks' | 'none';

export type CharmEffect =
  | { kind: 'row'; inset: number; extra: boolean }
  | { kind: 'plus' }
  | { kind: 'line'; inset: number; neighbor: boolean }
  | { kind: 'bloom'; count: number }
  | { kind: 'nest' }
  | { kind: 'wave'; depth: number; shift: number; trim: number; stripes: number }
  | { kind: 'cross' }
  | { kind: 'color' }
  | { kind: 'rows'; count: number; scoreScale: number }
  | { kind: 'storm'; count: number }
  | { kind: 'stag' }
  | { kind: 'owl' }
  | { kind: 'random'; count: number }
  | { kind: 'neighbors' }
  | { kind: 'mud' }
  | { kind: 'segment'; length: number }
  | { kind: 'column' }
  | { kind: 'square'; radius: number }
  | { kind: 'plus-reach'; reach: number }
  | { kind: 'diagonal' }
  | { kind: 'swap-rows' }
  | { kind: 'echo'; min: number; cap: number }
  | { kind: 'dive' }
  | { kind: 'leaves' }
  | { kind: 'corners'; size: number }
  | { kind: 'honey' }
  | { kind: 'lanes'; count: number }
  | { kind: 'shift-col'; by: number }
  | { kind: 'giant-cross' }
  | { kind: 'ring' }
  | { kind: 'wide-row'; count: number }
  | { kind: 'bottom-rows'; count: number }
  | { kind: 'ward' }
  | { kind: 'swap-meters' }
  | { kind: 'quake' }
  | { kind: 'colors'; count: number }
  | { kind: 'center'; radius: number }
  | { kind: 'densest'; radius: number }
  | { kind: 'bonus-row' }
  | { kind: 'half' }
  | { kind: 'picks' };

export interface CharmDef {
  id: string;
  name: string;
  tier: CharmTier;
  /** Trophies required before the Charm can be bought. Twig is open. */
  unlockAt: number;
  charge: number;
  blurb: string;
  effect: CharmEffect;
  /** How the player aims. `line` and `wave` are drags. */
  aim: CharmAim;
  prompt: string;
  /** Big clears score lighter and do not chain. */
  scoreScale: number;
  chain: boolean;
}

export const CHARMS: readonly CharmDef[] = [
  {
    id: 'pebble-skip',
    name: 'Pebble Skip',
    tier: 'twig',
    unlockAt: 0,
    charge: TIER_CHARGE.twig,
    blurb: 'Clears the middle 6 of a row.',
    effect: { kind: 'row', inset: 1, extra: false },
    aim: 'row',
    prompt: 'Tap a row',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'puffball',
    name: 'Puffball',
    tier: 'twig',
    unlockAt: 0,
    charge: TIER_CHARGE.twig,
    blurb: 'Clears a plus of 5 around a tap.',
    effect: { kind: 'plus' },
    aim: 'cell',
    prompt: 'Tap a tile',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'acorn-toss',
    name: 'Acorn Toss',
    tier: 'twig',
    unlockAt: 0,
    charge: TIER_CHARGE.twig,
    blurb: 'Drops acorns on 3 random tiles.',
    effect: { kind: 'random', count: 3 },
    aim: 'none',
    prompt: 'Tap to toss',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'dewdrop',
    name: 'Dewdrop',
    tier: 'twig',
    unlockAt: 0,
    charge: TIER_CHARGE.twig,
    blurb: 'Clears a tile and the four beside it.',
    effect: { kind: 'neighbors' },
    aim: 'cell',
    prompt: 'Tap a tile',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'skillet-slam',
    name: 'Skillet Slam',
    tier: 'copper',
    unlockAt: 100,
    charge: TIER_CHARGE.copper,
    blurb: 'Clears a row and one tile beside it.',
    effect: { kind: 'row', inset: 0, extra: true },
    aim: 'row',
    prompt: 'Tap a row',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'briar-cut',
    name: 'Briar Cut',
    tier: 'copper',
    unlockAt: 200,
    charge: TIER_CHARGE.copper,
    blurb: 'Clears a row or column, minus its ends.',
    effect: { kind: 'line', inset: 1, neighbor: false },
    aim: 'line',
    prompt: 'Drag a row or column',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'moss-rain',
    name: 'Fern Bloom',
    tier: 'copper',
    unlockAt: 350,
    charge: TIER_CHARGE.copper,
    blurb: 'Five tiles near a tap become your color.',
    effect: { kind: 'bloom', count: 5 },
    aim: 'cell',
    prompt: 'Tap where the fern blooms',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'mud-splash',
    name: 'Mud Splash',
    tier: 'copper',
    unlockAt: 150,
    charge: TIER_CHARGE.copper,
    blurb: 'Muddied tiles in a small cluster stay stuck for two turns.',
    effect: { kind: 'mud' },
    aim: 'cell',
    prompt: 'Tap the splash',
    scoreScale: 1,
    chain: false,
  },
  {
    id: 'thornwhip',
    name: 'Thornwhip',
    tier: 'copper',
    unlockAt: 280,
    charge: TIER_CHARGE.copper,
    blurb: 'Clears a short run of a row.',
    effect: { kind: 'segment', length: 4 },
    aim: 'row',
    prompt: 'Tap a row',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'firefly-flash',
    name: 'Firefly Flash',
    tier: 'copper',
    unlockAt: 450,
    charge: TIER_CHARGE.copper,
    blurb: 'Clears 5 random tiles.',
    effect: { kind: 'random', count: 5 },
    aim: 'none',
    prompt: 'Tap to flash',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'deep-dig',
    name: 'Deep Dig',
    tier: 'silver',
    unlockAt: 300,
    charge: TIER_CHARGE.silver,
    blurb: 'Clears a row or column, plus one tile.',
    effect: { kind: 'line', inset: 0, neighbor: true },
    aim: 'line',
    prompt: 'Drag a row or column',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'moon-glare',
    name: 'Moon Glare',
    tier: 'silver',
    unlockAt: 550,
    charge: TIER_CHARGE.silver,
    blurb: 'About seven tiles become your color.',
    effect: { kind: 'bloom', count: 7 },
    aim: 'cell',
    prompt: 'Tap where the glare blooms',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'hornet-nest',
    name: 'Hornet Nest',
    tier: 'silver',
    unlockAt: 800,
    charge: TIER_CHARGE.silver,
    blurb: 'Clears a 3×3 and leaves a bee bomb.',
    effect: { kind: 'nest' },
    aim: 'cell',
    prompt: 'Tap the nest',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'owl-hoot',
    name: 'Owl Hoot',
    tier: 'silver',
    unlockAt: 450,
    charge: TIER_CHARGE.silver,
    blurb: 'Clears a column.',
    effect: { kind: 'column' },
    aim: 'column',
    prompt: 'Tap a column',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'frost-bite',
    name: 'Frost Bite',
    tier: 'silver',
    unlockAt: 700,
    charge: TIER_CHARGE.silver,
    blurb: 'Freezes and shatters a 3×3.',
    effect: { kind: 'square', radius: 1 },
    aim: 'cell',
    prompt: 'Tap the frost',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'burrow-bomb',
    name: 'Burrow Bomb',
    tier: 'silver',
    unlockAt: 950,
    charge: TIER_CHARGE.silver,
    blurb: 'Clears a cross patch.',
    effect: { kind: 'plus-reach', reach: 2 },
    aim: 'cell',
    prompt: 'Tap the burrow',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'willow-sweep',
    name: 'Willow Sweep',
    tier: 'silver',
    unlockAt: 1100,
    charge: TIER_CHARGE.silver,
    blurb: 'Clears a diagonal.',
    effect: { kind: 'diagonal' },
    aim: 'cell',
    prompt: 'Tap the sweep',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'rabbit-hop',
    name: 'Rabbit Hop',
    tier: 'silver',
    unlockAt: 1250,
    charge: TIER_CHARGE.silver,
    blurb: 'Swaps two rows.',
    effect: { kind: 'swap-rows' },
    aim: 'row',
    prompt: 'Tap a row to hop',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'glide',
    name: 'Glide',
    tier: 'gold',
    unlockAt: 600,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears two lines, one band in.',
    effect: { kind: 'wave', depth: 2, shift: 1, trim: 0, stripes: 0 },
    aim: 'wave',
    prompt: 'Drag to aim the glide',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'dam-burst',
    name: 'Dam Burst',
    tier: 'gold',
    unlockAt: 1000,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears two edge lines and leaves two stripes.',
    effect: { kind: 'wave', depth: 2, shift: 0, trim: 0, stripes: 2 },
    aim: 'wave',
    prompt: 'Drag to burst the dam',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'thunder-stag',
    name: 'Thunder Stag',
    tier: 'gold',
    unlockAt: 1500,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears a full row and column.',
    effect: { kind: 'cross' },
    aim: 'cell',
    prompt: 'Tap the crossing',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'hollow-echo',
    name: 'Hollow Echo',
    tier: 'gold',
    unlockAt: 800,
    charge: TIER_CHARGE.gold,
    blurb: 'Repeats your last match.',
    effect: { kind: 'echo', min: 4, cap: 8 },
    aim: 'none',
    prompt: 'Tap to echo',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'falcon-dive',
    name: 'Falcon Dive',
    tier: 'gold',
    unlockAt: 1100,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears a line from above.',
    effect: { kind: 'dive' },
    aim: 'column',
    prompt: 'Tap a column',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'maple-storm',
    name: 'Maple Storm',
    tier: 'gold',
    unlockAt: 1300,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears a fall of leaves down the board.',
    effect: { kind: 'leaves' },
    aim: 'column',
    prompt: 'Tap where the leaves fall',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'badger-brawl',
    name: 'Badger Brawl',
    tier: 'gold',
    unlockAt: 1600,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears the four corners.',
    effect: { kind: 'corners', size: 2 },
    aim: 'none',
    prompt: 'Tap to brawl',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'honey-flood',
    name: 'Honey Flood',
    tier: 'gold',
    unlockAt: 1800,
    charge: TIER_CHARGE.gold,
    blurb: 'Turns one color to honey.',
    effect: { kind: 'honey' },
    aim: 'color',
    prompt: 'Tap a color',
    scoreScale: 0.75,
    chain: true,
  },
  {
    id: 'boulder-roll',
    name: 'Boulder Roll',
    tier: 'gold',
    unlockAt: 2000,
    charge: TIER_CHARGE.gold,
    blurb: 'Clears a lane two rows wide.',
    effect: { kind: 'lanes', count: 2 },
    aim: 'row',
    prompt: 'Tap a lane',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'river-rush',
    name: 'River Rush',
    tier: 'gold',
    unlockAt: 2200,
    charge: TIER_CHARGE.gold,
    blurb: 'Shifts a column.',
    effect: { kind: 'shift-col', by: 2 },
    aim: 'column',
    prompt: 'Tap a column',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'elder-root',
    name: 'Elder Root',
    tier: 'heartwood',
    unlockAt: 2500,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears every tile of one color.',
    effect: { kind: 'color' },
    aim: 'color',
    prompt: 'Tap a color',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'bear-quake',
    name: 'Bear Quake',
    tier: 'heartwood',
    unlockAt: 3500,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears three rows at three-quarter points.',
    effect: { kind: 'rows', count: 3, scoreScale: 0.75 },
    aim: 'row',
    prompt: 'Tap the middle row',
    scoreScale: 0.75,
    chain: false,
  },
  {
    id: 'mask-storm',
    name: 'Mask Storm',
    tier: 'heartwood',
    unlockAt: 4500,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Twelve tiles become your color, and two turn to stripes.',
    effect: { kind: 'storm', count: 12 },
    aim: 'cell',
    prompt: 'Tap the storm',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'ancient-oak',
    name: 'Ancient Oak',
    tier: 'heartwood',
    unlockAt: 2800,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears a giant cross.',
    effect: { kind: 'giant-cross' },
    aim: 'cell',
    prompt: 'Tap the oak',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'wolf-moon',
    name: 'Wolf Moon',
    tier: 'heartwood',
    unlockAt: 3200,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears every tile of one color.',
    effect: { kind: 'color' },
    aim: 'color',
    prompt: 'Tap a color',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'bramble-crown',
    name: 'Bramble Crown',
    tier: 'heartwood',
    unlockAt: 3800,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears the edge ring.',
    effect: { kind: 'ring' },
    aim: 'none',
    prompt: 'Tap to crown',
    scoreScale: 0.4,
    chain: false,
  },
  {
    id: 'elk-charge',
    name: 'Elk Charge',
    tier: 'heartwood',
    unlockAt: 4100,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears a wide row.',
    effect: { kind: 'wide-row', count: 3 },
    aim: 'row',
    prompt: 'Tap the charge',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'firefly-swarm',
    name: 'Firefly Swarm',
    tier: 'heartwood',
    unlockAt: 4400,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears 12 random tiles.',
    effect: { kind: 'random', count: 12 },
    aim: 'none',
    prompt: 'Tap to swarm',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'mudslide',
    name: 'Mudslide',
    tier: 'heartwood',
    unlockAt: 4700,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Clears the bottom 3 rows.',
    effect: { kind: 'bottom-rows', count: 3 },
    aim: 'none',
    prompt: 'Tap to slide',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'hornbeam-shield',
    name: 'Hornbeam Shield',
    tier: 'heartwood',
    unlockAt: 3000,
    charge: TIER_CHARGE.heartwood,
    blurb: "Blocks the rival's next Charm.",
    effect: { kind: 'ward' },
    aim: 'none',
    prompt: 'Tap to raise the shield',
    scoreScale: 1,
    chain: false,
  },
  {
    id: 'raven-trick',
    name: 'Raven Trick',
    tier: 'heartwood',
    unlockAt: 4200,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Swaps the meters.',
    effect: { kind: 'swap-meters' },
    aim: 'none',
    prompt: 'Tap to trick',
    scoreScale: 1,
    chain: false,
  },
  {
    id: 'mossquake',
    name: 'Mossquake',
    tier: 'heartwood',
    unlockAt: 4900,
    charge: TIER_CHARGE.heartwood,
    blurb: 'Shuffles the board and clears the matches.',
    effect: { kind: 'quake' },
    aim: 'none',
    prompt: 'Tap to quake',
    scoreScale: 1,
    chain: true,
  },
  {
    id: 'starlit-stag',
    name: 'Starlit Stag',
    tier: 'starlit',
    unlockAt: 5000,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears two colors and leaves a crown bomb.',
    effect: { kind: 'stag' },
    aim: 'none',
    prompt: 'Tap to loose the stag',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'comet-owl',
    name: 'Comet Owl',
    tier: 'starlit',
    unlockAt: 5000,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears an X and a row, and leaves two bombs.',
    effect: { kind: 'owl' },
    aim: 'cell',
    prompt: 'Tap the comet',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'aurora-fox',
    name: 'Aurora Fox',
    tier: 'starlit',
    unlockAt: 5000,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears two colors.',
    effect: { kind: 'colors', count: 2 },
    aim: 'none',
    prompt: 'Tap to loose the fox',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'moonlit-lynx',
    name: 'Moonlit Lynx',
    tier: 'starlit',
    unlockAt: 5200,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears the center 5×5.',
    effect: { kind: 'center', radius: 2 },
    aim: 'none',
    prompt: 'Tap the moonlight',
    scoreScale: 0.45,
    chain: false,
  },
  {
    id: 'meteor-bear',
    name: 'Meteor Bear',
    tier: 'starlit',
    unlockAt: 5500,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears the densest area.',
    effect: { kind: 'densest', radius: 2 },
    aim: 'none',
    prompt: 'Tap to call the meteor',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'star-hare',
    name: 'Star Hare',
    tier: 'starlit',
    unlockAt: 5800,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears a row, then grants one extra move.',
    effect: { kind: 'bonus-row' },
    aim: 'row',
    prompt: 'Tap a row',
    scoreScale: 0.5,
    chain: false,
  },
  {
    id: 'celestial-tortoise',
    name: 'Celestial Tortoise',
    tier: 'starlit',
    unlockAt: 6200,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears half the board.',
    effect: { kind: 'half' },
    aim: 'none',
    prompt: 'Tap the shell',
    scoreScale: 0.35,
    chain: false,
  },
  {
    id: 'wish-willow',
    name: 'Wish Willow',
    tier: 'starlit',
    unlockAt: 6500,
    charge: TIER_CHARGE.starlit,
    blurb: 'Clears 3 tiles you pick.',
    effect: { kind: 'picks' },
    aim: 'picks',
    prompt: 'Tap 3 tiles',
    scoreScale: 1,
    chain: true,
  },
];

const BY_ID = new Map(CHARMS.map((charm) => [charm.id, charm]));

export function charmById(id: string | null | undefined): CharmDef | null {
  if (!id) return null;
  return BY_ID.get(id) ?? null;
}

export function charmsOfTier(tier: CharmTier): CharmDef[] {
  return CHARMS.filter((charm) => charm.tier === tier);
}

export function charmGesture(id: string): 'tap' | 'drag' {
  const charm = charmById(id);
  if (!charm) return 'tap';
  return charm.aim === 'line' || charm.aim === 'wave' ? 'drag' : 'tap';
}

export const WELCOME_CHARM_PACK: Readonly<Record<string, number>> = {
  'skillet-slam': 2,
  'briar-cut': 1,
  'moss-rain': 1,
};

/** Glen band → the Charm tier a bot is allowed to bring. */
export function botTierForTrophies(best: number): CharmTier {
  if (best >= 5000) return 'starlit';
  if (best >= 2500) return 'heartwood';
  if (best >= 700) return 'gold';
  if (best >= 400) return 'silver';
  if (best >= 80) return 'copper';
  return 'twig';
}

export function botCharmId(best: number, salt: number): string {
  const pool = charmsOfTier(botTierForTrophies(best));
  const pick = pool[Math.abs(salt) % Math.max(1, pool.length)];
  return pick?.id ?? 'pebble-skip';
}
